import { createClient } from "@supabase/supabase-js";
import { trocarOrigemStorage } from "./links";

const urlConfigurada = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const local = (h: string) => h === "localhost" || h === "127.0.0.1";
/** Supabase deste computador ou da rede interna (IP 10.x, 172.16-31.x, 192.168.x). */
const daRede = (h: string) => local(h) || /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(h);

/**
 * Supabase local rodando com o Vite (npm run dev, iniciar-wms.bat): o app chama o Supabase pelo
 * próprio endereço e o Vite repassa para a porta 54321 do computador (proxy no vite.config.ts).
 * Vale no computador, no celular pela rede e pelo túnel: sem CORS, sem depender do IP do PC
 * (que muda) nem de localhost x 127.0.0.1. No app publicado (nuvem ou VPS) a URL configurada
 * vale como está.
 */
function urlDoSupabase(): string | undefined {
  if (!urlConfigurada || typeof window === "undefined") return urlConfigurada;
  try {
    const host = new URL(urlConfigurada).hostname;
    // build de produção (VPS): só o endereço local vira o do app; IP da rede é usado como está
    if ((import.meta.env.DEV && daRede(host)) || (local(host) && !local(window.location.hostname))) {
      return window.location.origin;
    }
  } catch {
    return urlConfigurada;
  }
  return urlConfigurada;
}
const url = urlDoSupabase();

/**
 * Link do Storage que o navegador consegue abrir. No Supabase local as funções assinam os links
 * com o endereço interno do Docker (http://kong:8000), que o navegador não alcança: troca pela
 * origem do Supabase que o app usa. Link externo (transportadora) passa direto.
 */
export function linkAcessivel(link: string): string {
  return trocarOrigemStorage(link, url, typeof window === "undefined" ? undefined : window.location.origin);
}
const chave = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const configurado = Boolean(url && chave);

export const supabase = createClient(url ?? "http://localhost:54321", chave ?? "sem-chave", {
  auth: { persistSession: true, autoRefreshToken: true },
});

/**
 * O Supabase responde? (tela de "sem conexão"): "fora" sem resposta nenhuma, "chave" quando
 * responde mas recusa a chave do web/.env.local, "ok" quando está no ar.
 */
export async function supabaseResponde(): Promise<"ok" | "chave" | "fora"> {
  const controle = new AbortController();
  const limite = setTimeout(() => controle.abort(), 4000);
  try {
    const r = await fetch(`${url ?? "http://localhost:54321"}/auth/v1/health`, { headers: { apikey: chave ?? "" }, signal: controle.signal });
    return r.ok ? "ok" : r.status === 401 || r.status === 403 ? "chave" : "fora";
  } catch {
    return "fora";
  } finally {
    clearTimeout(limite);
  }
}

/** Apaga a sessão guardada neste navegador (sem falar com o servidor) e recarrega. */
export function esquecerSessaoLocal() {
  try {
    for (const k of Object.keys(localStorage)) if (/^sb-.*-auth-token/.test(k)) localStorage.removeItem(k);
  } catch {
    // navegador sem localStorage: só recarrega
  }
  window.location.reload();
}

export const dominioLogin = (import.meta.env.VITE_LOGIN_DOMAIN as string | undefined) ?? "wms.oficial.local";

/** "joao" vira "joao@wms.oficial.local"; e-mail completo passa direto. */
export function emailDoUsuario(usuario: string): string {
  const u = usuario.trim().toLowerCase();
  return u.includes("@") ? u : `${u}@${dominioLogin}`;
}
